import { api } from '../api/client';

import { API_ITEMS_URL } from '../utils';
import { markNotReadable, markReadable, parseNotReadableHeader } from '../utils/linkedRecords';

/**
 * Many item writes in one submit (`POST /api/items/bulk`).
 *
 * The worklist's write path, and the "apply to the selected rows" gesture.
 * Three things about the contract are worth knowing at the call site:
 *
 *  - validation is ALL-OR-NOTHING (a 400 means nothing was written, and its
 *    `rows` array carries a message per offending row index AND, where the
 *    row named an item, that item's `_id`);
 *  - EVERY refusal is asked before the first write — the write grant, the
 *    element shapes, `required`, the record lock, the attestation a signing
 *    card demands, and the delete policy — so only a RACE between two
 *    requests answers 200 with `partial: true` (`failedAt.code` is
 *    `write_failed` when the database stopped answering and `stale` when
 *    somebody else saved a row mid-apply), which is why callers render
 *    `written` rather than assuming the whole list landed;
 *  - TRIAGE rides the same endpoint: `{ _id, op: 'accept' }` / `{ _id, op:
 *    'reject', reason? }` decide intake submissions (a select names the queue
 *    with `review: 'pending'`); the dry-run summary gains `accept` / `reject`
 *    and `written[].action` is 'accepted' / 'rejected'.
 *  - `merge` defaults TRUE, so a row that names two elements leaves the rest
 *    of the item alone. Sending `merge: false` is the destructive path — and
 *    only for the elements a row actually NAMES: a row with no `elements` key
 *    at all keeps the stored map in either mode, and a dry run's `blanks`
 *    says how many values a replace row would empty by silence.
 */

// The endpoint's own ceilings (utils/itemBulkUtils.js). Stated here so the
// panel can refuse BEFORE the request rather than surface a 400 as a
// surprise, and so "narrow the filter" is the same sentence on both sides.
export const MAX_ROWS = 200;
export const BULK_FILTER_MAX = 1000;
// GET /api/items?ids= is capped at this (queryContract MAX_BATCH_IDS), which
// is what re-resolving a selection whose rows have scrolled out has to chunk.
export const MAX_BATCH_IDS = 100;

/** Split a list into runs of at most `size`. */
export function chunk(list, size) {
	const out = [];
	for (let i = 0; i < (list || []).length; i += size) out.push(list.slice(i, i + size));
	return out;
}

/**
 * A client-minted batch id. `requestId` is minted per HTTP REQUEST, so a
 * selection the client had to split across several calls loses the tie that
 * makes "what else did that click do" answerable; this restores it, and
 * `GET /api/history?batchId=` is where it is asked.
 */
export function newBatchId() {
	const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
		? crypto.randomUUID()
		: null;
	if (uuid) return uuid;
	// jsdom, and any browser without randomUUID. Shape matters (the API takes
	// a uuid), randomness only has to be enough to not collide with the same
	// user's other batches.
	const hex = n => Math.floor(Math.random() * 16 ** n).toString(16).padStart(n, '0');
	return `${hex(8)}-${hex(4)}-4${hex(3)}-a${hex(3)}-${hex(12)}`;
}

/**
 * The `rows` selector: a list the client built, one instruction per item.
 * `merge` is left to the server's default (true) unless a caller says
 * otherwise, because saying `merge: false` out loud is the point of the flag.
 */
export async function bulkWriteItems(collectionId, rows, {
	dryRun = false, merge, batchId, reason,
} = {}) {
	const { data } = await api.post(`${API_ITEMS_URL}/bulk`, {
		collection_id: collectionId,
		rows,
		...(dryRun ? { dryRun: true } : {}),
		...(merge === undefined ? {} : { merge }),
		...(batchId ? { batchId } : {}),
		...(reason ? { reason } : {}),
	});
	return data;
}

/**
 * The `select` + `patch` selector: ONE operation applied to everything
 * matching the list's own query, resolved server-side. `expectedCount` is
 * the count the user was looking at — a disagreement is a 409
 * `count_mismatch` rather than an apply to a set they never saw.
 *
 * `select.exclude` subtracts ids from the match, and is what makes a refusal
 * on this path survivable: all-or-nothing means one locked row refuses all
 * 412, and the client holds no ids here to re-select the other 411 with. The
 * ids the 400 names go straight back in. `expectedCount` stays the FILTER's
 * own total, counted before the subtraction.
 *
 * This is the path that does NOT need chunking, which is why it exists: one
 * request is one validation pass, and all-or-nothing survives. Above
 * BULK_FILTER_MAX the API refuses with "narrow the filter" — deliberately,
 * rather than letting the client loop over thousands behind the user's back.
 */
export async function bulkApplyToFilter(collectionId, select, patch, {
	dryRun = false, merge, batchId, reason,
} = {}) {
	const { data } = await api.post(`${API_ITEMS_URL}/bulk`, {
		collection_id: collectionId,
		select,
		patch,
		...(dryRun ? { dryRun: true } : {}),
		...(merge === undefined ? {} : { merge }),
		...(batchId ? { batchId } : {}),
		...(reason ? { reason } : {}),
	});
	return data;
}

/**
 * Apply one patch to a list of ids, in chunks of MAX_ROWS.
 *
 * DRY-RUN EVERY CHUNK FIRST, COMMIT ONLY IF ALL OF THEM ARE CLEAN. That
 * ordering is the whole design: a 400 on chunk 7 after chunks 1-6 have
 * committed is exactly the half-saved state this endpoint exists to prevent,
 * and the dry pass is the only way to find it before anything lands.
 *
 * Chunking still breaks all-or-nothing ACROSS chunks — a database failure
 * during chunk 4 leaves 1-3 written — and the caller MUST say so in those
 * words rather than report a clean success. `onProgress` is called with
 * `{ phase, done, total }` so a long apply can say where it is.
 *
 * Resolves `{ ok, batchId, dryRun, written, summary, chunks, committed }`,
 * or throws the first chunk's error with nothing written.
 */
export async function applyToIds(collectionId, ids, patch, {
	merge, reason, onProgress,
} = {}) {
	const batches = chunk(ids || [], MAX_ROWS);
	const batchId = newBatchId();
	const rowsFor = batch => batch.map(id => ({ ...patch, _id: id }));

	const dry = [];
	for (let i = 0; i < batches.length; i += 1) {
		onProgress?.({ phase: 'checking', done: i, total: batches.length });
		// eslint-disable-next-line no-await-in-loop
		dry.push(await bulkWriteItems(collectionId, rowsFor(batches[i]), {
			dryRun: true, merge, reason,
		}));
	}

	const written = [];
	let committed = 0;
	for (let i = 0; i < batches.length; i += 1) {
		onProgress?.({ phase: 'saving', done: i, total: batches.length });
		try {
			// eslint-disable-next-line no-await-in-loop
			const result = await bulkWriteItems(collectionId, rowsFor(batches[i]), {
				merge, batchId, reason,
			});
			written.push(...(result?.written || []));
			committed += 1;
			if (result?.partial) {
				return { ok: false, partial: true, batchId, written, chunks: batches.length, committed, failedAt: result.failedAt };
			}
		} catch (error) {
			// Past the first commit this is the state the panel has to name:
			// some chunks landed and this one did not.
			if (committed > 0) {
				const err = error instanceof Error ? error : new Error('Bulk apply failed');
				err.partialBatch = { batchId, written, chunks: batches.length, committed };
				throw err;
			}
			throw error;
		}
	}

	return {
		ok: true,
		batchId,
		written,
		chunks: batches.length,
		committed,
		summary: mergeSummaries(dry),
	};
}

/** Add up the per-chunk dry-run summaries into one. `accept` / `reject`
 * appear only on a triage plan (intake submissions), and add up the same. */
function mergeSummaries(results) {
	return (results || []).reduce((acc, r) => ({
		create: acc.create + (r?.summary?.create || 0),
		update: acc.update + (r?.summary?.update || 0),
		delete: acc.delete + (r?.summary?.delete || 0),
		blanks: acc.blanks + (r?.summary?.blanks || 0),
		accept: acc.accept + (r?.summary?.accept || 0),
		reject: acc.reject + (r?.summary?.reject || 0),
	}), { create: 0, update: 0, delete: 0, blanks: 0, accept: 0, reject: 0 });
}

/**
 * Dry-run a patch over a list of ids, chunked, and return one combined plan.
 * A refusal in ANY chunk is reported as a refusal of the whole apply —
 * nothing is written by a dry run, so this is free and is what the panel
 * shows before the commit button lights.
 *
 * Row indexes are chunk-local, so each chunk's refusals are re-keyed to the
 * item ids they name before they are merged.
 */
export async function dryRunIds(collectionId, ids, patch, { merge, reason } = {}) {
	const batches = chunk(ids || [], MAX_ROWS);
	const rows = [];
	const refusals = [];
	const summary = { create: 0, update: 0, delete: 0, blanks: 0, accept: 0, reject: 0 };

	for (let i = 0; i < batches.length; i += 1) {
		const offset = i * MAX_ROWS;
		try {
			// eslint-disable-next-line no-await-in-loop
			const result = await bulkWriteItems(collectionId, batches[i].map(id => ({ ...patch, _id: id })), {
				dryRun: true, merge, reason,
			});
			(result?.rows || []).forEach(row => rows.push({ ...row, index: offset + row.index }));
			summary.create += result?.summary?.create || 0;
			summary.update += result?.summary?.update || 0;
			summary.delete += result?.summary?.delete || 0;
			// How many stored values a replace would empty by silence, across
			// every chunk. Always 0 on the merge default; dropping it would
			// lose the only number that says what `merge: false` costs.
			summary.blanks += result?.summary?.blanks || 0;
			// A triage plan (op 'accept' | 'reject' on intake submissions).
			summary.accept += result?.summary?.accept || 0;
			summary.reject += result?.summary?.reject || 0;
		} catch (error) {
			const list = rowErrorList(error);
			if (!list.length) throw error;
			list.forEach(row => refusals.push({
				...row,
				index: offset + (Number(row.index) || 0),
				_id: row._id || batches[i][Number(row.index) || 0],
			}));
		}
	}

	return { ok: refusals.length === 0, rows, refusals, summary };
}

/** The raw per-row refusals a 400 carries, as a flat list. */
export function rowErrorList(error) {
	const rows = error?.response?.data?.rows;
	return Array.isArray(rows) ? rows : [];
}

/** The per-row messages a 400 carries, keyed by row index — the shape the
 * worklist renders beside its rows. */
export function rowErrorsOf(error) {
	const byIndex = {};
	rowErrorList(error).forEach(row => {
		const index = Number(row?.index);
		if (!Number.isFinite(index)) return;
		byIndex[index] = byIndex[index] || [];
		byIndex[index].push(row);
	});
	return byIndex;
}

/** The same refusals keyed by ITEM id — what a selection panel renders. */
export function rowErrorsById(error) {
	const byId = {};
	rowErrorList(error).forEach(row => {
		const id = row?._id;
		if (!id) return;
		byId[id] = byId[id] || [];
		byId[id].push(row);
	});
	return byId;
}

/**
 * Re-resolve selected items the feed no longer holds (scrolled past, filtered
 * away, on a page that has not loaded). `GET /api/items?ids=` is capped at
 * MAX_BATCH_IDS, so this chunks too.
 */
export async function fetchItemsByIds(ids) {
	const out = [];
	for (const batch of chunk(ids || [], MAX_BATCH_IDS)) {
		// eslint-disable-next-line no-await-in-loop
		const { data, headers } = await api.get(API_ITEMS_URL, { params: { ids: batch.join(',') } });
		const rows = Array.isArray(data) ? data : data?.items || [];
		// The tombstone's answer (keepr-api § Lookups across a link): ids asked
		// for and not returned — every chip for them says "No longer available".
		markNotReadable(parseNotReadableHeader(headers));
		markReadable(rows.map(row => row?._id));
		out.push(...rows);
	}
	return out;
}

/**
 * Every item a list shows, for a pass that must know each one (bulk attach,
 * KPR-187: its titles and values decide which item a file is for). The feed's
 * own route and parameters, paged at the API's 200, up to `max` — beyond that
 * the caller says so and asks for a narrower filter rather than fetching a
 * whole collection. `exclude` drops ids (an "all matching" selection minus
 * the rows taken out of it). Linked rows are never asked for: another
 * collection's records are read-only here.
 * Resolves `{ items, total, truncated }`.
 */
export const MATCHING_PAGE = 200;
export async function fetchMatchingItems({ collectionId, query = '', cardFilter = '', review = '', exclude = [] } = {}, { max = BULK_FILTER_MAX } = {}) {
	const skipIds = new Set(exclude || []);
	const items = [];
	let total = null;
	for (let skip = 0; items.length < max; skip += MATCHING_PAGE) {
		const params = { collection_id: collectionId, limit: MATCHING_PAGE, skip };
		if (cardFilter) params.card_id = cardFilter;
		if (query) params.q = query;
		if (review) params.review = review;
		// eslint-disable-next-line no-await-in-loop
		const { data, headers } = await api.get(API_ITEMS_URL, { params });
		const rows = Array.isArray(data) ? data : data?.items || [];
		const counted = Number(headers?.['x-total-count']);
		if (Number.isFinite(counted)) total = counted;
		for (const row of rows) {
			if (!skipIds.has(row?._id)) items.push(row);
		}
		if (rows.length < MATCHING_PAGE) break;
	}
	const truncated = items.length > max || (total !== null && total - skipIds.size > max);
	return { items: items.slice(0, max), total: total ?? items.length, truncated };
}
